import { Check, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import Icon from "../components/Icon";
import { useCart } from "../context/CartContext";
import { PLANS, CATALOG_ITEMS, formatPrice } from "../data/catalog";

const PERKS = [
  { icon: "Percent", title: "Save 15%", desc: "On every subscription delivery, forever." },
  { icon: "Truck", title: "Free EU Shipping", desc: "On all subscription orders." },
  { icon: "RefreshCw", title: "Flexible Delivery", desc: "Pause, skip or reschedule anytime." },
  { icon: "Gift", title: "Free Welcome Gift", desc: "A NARA glass bottle with your first box." },
  { icon: "XCircle", title: "Cancel Anytime", desc: "No commitment, no cancellation fees." },
  { icon: "Headphones", title: "Priority Support", desc: "Dedicated subscriber care team." },
];

const SUB_BENEFITS = [
  "Save 15% on every delivery",
  "Free EU shipping, always",
  "Free welcome gift on your first box",
  "Pause, skip or cancel anytime",
];

const Bundles = () => {
  const { addItem } = useCart();
  const subPlans = PLANS.filter((p) => CATALOG_ITEMS[p.id].subscribable);
  const starter = PLANS.find((p) => !CATALOG_ITEMS[p.id].subscribable);

  const addSub = (id) => { addItem(id, "subscribe"); toast.success("Subscription added to cart"); };
  const addOnce = (id) => { addItem(id, "once"); toast.success("Added to cart"); };

  return (
    <div>
      <PageHero
        overline="Subscribe & Save 15%"
        title="Your daily ritual, on autopilot."
        subtitle="Choose a plan and never run out. Save 15% on every delivery, get free EU shipping, and pause, skip or cancel anytime."
      />

      {/* SUBSCRIPTION PLANS (primary) */}
      <section className="max-w-5xl mx-auto px-4 md:px-8 py-16 md:py-20">
        <div className="flex items-center justify-center mb-10">
          <span className="inline-flex items-center gap-2 bg-sky-50 text-royal text-sm font-semibold px-4 py-2 rounded-full">
            <Sparkles className="w-4 h-4" /> Most members choose a subscription
          </span>
        </div>

        <div className="grid md:grid-cols-2 gap-6 lg:gap-8">
          {subPlans.map((p, i) => {
            const it = CATALOG_ITEMS[p.id];
            return (
              <Reveal key={p.id} delay={i * 0.1}>
                <div
                  data-testid={`plan-card-${p.id}`}
                  className={`relative bg-white rounded-3xl p-8 border-2 flex flex-col h-full transition-all duration-300 ${
                    p.bestValue ? "border-royal shadow-2xl shadow-royal/10" : "border-slate-200 hover:border-blue-200 hover:shadow-xl"
                  }`}
                >
                  {(p.popular || p.bestValue) && (
                    <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-royal text-white text-[10px] font-bold tracking-widest uppercase px-4 py-1.5 rounded-full whitespace-nowrap">
                      {p.bestValue ? "Best Value" : "Most Popular"}
                    </span>
                  )}
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold tracking-[0.18em] uppercase text-royal">{it.supply}</p>
                      <h3 className="font-heading text-2xl font-bold text-navy-900 mt-1">{it.name}</h3>
                      <p className="text-sm text-slate-400 mt-1">{it.sachets} · delivered {p.frequency}</p>
                    </div>
                    <img src={it.image} alt={it.name} className="h-20 object-contain shrink-0" />
                  </div>

                  <div className="mt-6 flex items-end gap-2">
                    <span className="font-heading text-4xl font-bold text-navy-900">{formatPrice(it.subscribePrice)}</span>
                    <span className="text-slate-400 line-through mb-1">{formatPrice(it.price)}</span>
                  </div>
                  <p className="text-sm font-medium text-emerald-600 mt-1">Save 15% · {formatPrice(p.perServingSub)}/serving</p>

                  <ul className="mt-6 space-y-2.5 flex-1">
                    {SUB_BENEFITS.map((b) => (
                      <li key={b} className="flex items-start gap-2.5 text-sm text-slate-600">
                        <Check className="w-4 h-4 text-royal mt-0.5 shrink-0" /> {b}
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => addSub(p.id)}
                    data-testid={`plan-subscribe-${p.id}`}
                    className="mt-7 w-full rounded-full px-6 py-3.5 font-medium bg-royal text-white hover:bg-royal-600 transition-all"
                  >
                    Subscribe & Save
                  </button>
                  <button
                    onClick={() => addOnce(p.id)}
                    data-testid={`plan-once-${p.id}`}
                    className="mt-3 w-full text-center text-sm text-slate-400 hover:text-navy-900 transition-colors"
                  >
                    or buy once for {formatPrice(it.price)}
                  </button>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* SECONDARY: one-time starter */}
      {starter && (
        <section className="max-w-5xl mx-auto px-4 md:px-8 pb-16">
          <Reveal>
            <div className="rounded-3xl bg-[#FAFBFD] border border-slate-100 p-6 md:p-8 flex flex-col sm:flex-row items-center gap-6" data-testid="starter-card">
              <img src={CATALOG_ITEMS[starter.id].image} alt={CATALOG_ITEMS[starter.id].name} className="h-24 object-contain" />
              <div className="flex-1 text-center sm:text-left">
                <span className="text-xs font-bold tracking-[0.18em] uppercase text-slate-400">New to NARA?</span>
                <h3 className="font-heading text-xl font-semibold text-navy-900 mt-1">Try the 1-Week Starter</h3>
                <p className="text-sm text-slate-500 mt-1">{CATALOG_ITEMS[starter.id].sachets} · one-time purchase, no commitment.</p>
              </div>
              <div className="text-center shrink-0">
                <p className="font-heading text-3xl font-bold text-navy-900">{formatPrice(CATALOG_ITEMS[starter.id].price)}</p>
                <button
                  onClick={() => addOnce(starter.id)}
                  data-testid="starter-add"
                  className="mt-2 rounded-full px-6 py-3 font-medium bg-navy-900 text-white hover:bg-navy-800 transition-all text-sm"
                >
                  Add to Cart
                </button>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* WHY SUBSCRIBE */}
      <section className="bg-navy-900 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 md:py-20">
          <Reveal className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-bold tracking-[0.22em] uppercase text-sky-300 mb-3 block">Why Subscribe</span>
            <h2 className="font-heading text-3xl md:text-4xl font-semibold tracking-tight">Membership has its benefits.</h2>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
            {PERKS.map((perk, i) => (
              <Reveal key={perk.title} delay={i * 0.06}>
                <div className="flex items-start gap-4 bg-white/5 rounded-2xl p-6 border border-white/10 h-full">
                  <span className="flex items-center justify-center w-11 h-11 rounded-xl bg-sky-400/15 text-sky-300 shrink-0">
                    <Icon name={perk.icon} className="w-5 h-5" />
                  </span>
                  <div>
                    <h3 className="font-heading font-semibold">{perk.title}</h3>
                    <p className="text-sm text-slate-400 mt-1">{perk.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal className="text-center mt-12">
            <p className="text-slate-400 text-sm flex items-center justify-center gap-2">
              Prefer a single order? <span className="text-white">One-time purchase available at checkout.</span>
              <ArrowRight className="w-4 h-4 text-sky-300" />
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
};

export default Bundles;
